import type { IAiSpreadsheetCell } from "@/payroll/ai-script/types";
import { getAiScriptWorkbookAuthorizationMessage } from "@/payroll/ai-script/utils";
import {
  type WorkbookCellEditBatchInfo,
  WorkbookCellEditBatchError,
} from "@/payroll/ai-script/workbook-cell-edit-batcher";

export interface CellEditNotification {
  title: string;
  description: string;
}

interface CellEditToastFeedback {
  title: string;
  description: string;
  id?: string;
}

export interface WorkbookMutationErrorFeedback extends CellEditToastFeedback {
  error: unknown;
}

export const getWorkbookMutationErrorFeedback = (
  defaultTitle: string,
  mutationError: unknown,
): WorkbookMutationErrorFeedback => {
  const batchError =
    mutationError instanceof WorkbookCellEditBatchError ? mutationError : null;
  const error = batchError ? batchError.cause : mutationError;

  return {
    error,
    title:
      batchError && batchError.batch.editCount > 1
        ? `Failed to update ${batchError.batch.editCount} cells`
        : defaultTitle,
    description: getAiScriptWorkbookAuthorizationMessage(error),
    id: batchError?.batch.id,
  };
};

export const getCellEditNotificationFeedback = (
  notification: CellEditNotification,
  batch: WorkbookCellEditBatchInfo,
): CellEditToastFeedback =>
  batch.editCount > 1
    ? {
        title: "Cell edits saved with a warning",
        description: `${batch.editCount} cells were updated. ${notification.description}`,
        id: batch.id,
      }
    : { ...notification, id: batch.id };

const uniqueNotifications = (notifications: CellEditNotification[]) =>
  Array.from(
    new Map(
      notifications.map((notification) => [
        `${notification.title}:${notification.description}`,
        notification,
      ]),
    ).values(),
  );

const getCellEditBatchNotificationFeedback = (
  notifications: CellEditNotification[],
  batch: WorkbookCellEditBatchInfo,
): CellEditToastFeedback | undefined => {
  const unique = uniqueNotifications(notifications);
  const first = unique[0];
  if (!first) return undefined;
  if (unique.length === 1) {
    return getCellEditNotificationFeedback(first, batch);
  }

  return {
    title: "Cell edits saved with warnings",
    description: `${batch.editCount} cells were updated. ${unique
      .map(({ description }) => description)
      .join(" ")}`,
    id: batch.id,
  };
};

interface PendingCellEditBatchFeedback {
  remaining: number;
  notifications: CellEditNotification[];
}

export const createCellEditBatchFeedbackCollector = () => {
  const successes = new Map<string, PendingCellEditBatchFeedback>();
  const errors = new Map<string, number>();

  return {
    completeSuccess: (
      batch: WorkbookCellEditBatchInfo,
      notification: CellEditNotification | null,
    ): CellEditToastFeedback | undefined => {
      const pending = successes.get(batch.id) ?? {
        remaining: batch.requestCount,
        notifications: [],
      };
      if (notification) pending.notifications.push(notification);
      pending.remaining -= 1;
      if (pending.remaining > 0) {
        successes.set(batch.id, pending);
        return undefined;
      }

      successes.delete(batch.id);
      return getCellEditBatchNotificationFeedback(pending.notifications, batch);
    },
    completeError: (
      mutationError: unknown,
      defaultTitle: string,
    ): WorkbookMutationErrorFeedback | undefined => {
      if (!(mutationError instanceof WorkbookCellEditBatchError)) {
        return getWorkbookMutationErrorFeedback(defaultTitle, mutationError);
      }

      const remaining =
        (errors.get(mutationError.batch.id) ??
          mutationError.batch.requestCount) - 1;
      if (remaining > 0) {
        errors.set(mutationError.batch.id, remaining);
        return undefined;
      }

      errors.delete(mutationError.batch.id);
      return getWorkbookMutationErrorFeedback(defaultTitle, mutationError);
    },
  };
};

export const getCellEditNotification = ({
  cell,
  wasFormula,
}: {
  cell: IAiSpreadsheetCell | undefined;
  wasFormula: boolean;
}): CellEditNotification | null => {
  if (!cell) return null;

  if (!cell.override && !cell.manual_row_id) {
    return {
      title: "Edit saved for this run only",
      description:
        "This cell can't be tracked, so the change won't be re-applied when payroll re-runs.",
    };
  }

  if (wasFormula) {
    return {
      title: "Calculated value replaced with a fixed number",
      description:
        "This cell was calculated by a formula. Your value will be kept on re-runs instead of recalculating.",
    };
  }

  return null;
};
